import { Component } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CONTINUE_WITH_APPLE_SIGN_IN_ROUTE,
  CONTINUE_WITH_GOOGLE_SIGN_IN_ROUTE,
  DEFAULT_ROUTE_CONSTANT,
  DESTINATION_KEY_CONSTANT,
  EMAIL_VERIFICATION_CTA_ROUTE, FRESH_FROM_SOURCE_CONSTANT,
  LOGIN_OBJECT_KEY_CONSTANT,
  LOGIN_SUCCESSFUL_ROUTE,
  PREVIOUS_DESTINATION_KEY_CONSTANT,
  PRODUCT_KEY_CONSTANT,
  REDIRECTED_FLAG_CONSTANT,
} from '../constants/routes.constant';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {Subject, from, map, mergeMap, takeLast} from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import {
  ONBOARDING_ID_TOKEN_KEY,
  SESSION_TOKEN_KEY,
} from '../constants/local-storage.constant';
import { PROGRESS_BAR_SUBJECT } from '../app.component';
import { HttpStatusCode } from "@angular/common/http";
import {UserProfileService} from "../services/user-profile.service";

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent {
  public hide: boolean = true;
  public inProgress: boolean = false;
  public form!: FormGroup;
  public verifyEmailCtaRoute: string = EMAIL_VERIFICATION_CTA_ROUTE;
  private readonly loginSuccessfulRoute: string = LOGIN_SUCCESSFUL_ROUTE;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  private previousDestination!: string;
  private readonly productNameKey: string = PRODUCT_KEY_CONSTANT;
  private readonly destinationKey: string = DESTINATION_KEY_CONSTANT;
  private readonly loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private readonly previousDestinationKey: string = PREVIOUS_DESTINATION_KEY_CONSTANT;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
    [this.previousDestinationKey]: this.previousDestination,
  };
  public showError: boolean = false;
  public errorText: string =
    'Unable to log you in. Please confirm you provided the correct details';
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;
  private signInWithGoogleRoute: string = CONTINUE_WITH_GOOGLE_SIGN_IN_ROUTE;
  private signInWithAppleRoute: string = CONTINUE_WITH_APPLE_SIGN_IN_ROUTE;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly firebaseAuthService: FirebaseAuthService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    private readonly userProfileService: UserProfileService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    // TODO to be removed later
    localStorage.removeItem(REDIRECTED_FLAG_CONSTANT);

    this.initializeForm();

    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;
    this.loginObject = this.activatedRoute.snapshot.queryParamMap.get(
      this.loginObjectKey
    )!;
    this.previousDestination = this.activatedRoute.snapshot.queryParamMap.get(
      this.previousDestinationKey
    )!;

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
      [this.previousDestinationKey]: this.previousDestination,
    };

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
      });
    }
  }

  public signInWithEmailAndPassword() {
    this.inProgress = true;
    this.showError = false;

    const value = this.form.getRawValue();

    this.subject.next(true);

    this.firebaseAuthService
      .signInWithEmailAndPassword(value.email, value.password)
      .pipe(
        mergeMap((userCredentials) => {
          return this.firebaseAuthService
            .getIdToken()
            .pipe(map((idTokenResult) => ({ userCredentials, idTokenResult })));
        }),
        mergeMap((data) => {
          const idTokenResult = data.idTokenResult;

          // INFO isVerified
          if (data.userCredentials.user?.emailVerified) {
            // INFO call for authenticated user
            return this.authenticationService
              .authenticateUser(idTokenResult.token)
              .pipe(
                mergeMap((token) => {
                  localStorage.setItem(SESSION_TOKEN_KEY, token);

                  return this.router.navigate(
                    ['/', ...this.loginSuccessfulRoute.split('/')],
                    {
                      queryParams: this.queryParams,
                      queryParamsHandling: 'preserve',
                    }
                  );
                })
              );
          }

          // INFO Always unverified
          return this.userProfileService
            .sendEmailVerification(
              value.email,
              this.productName,
              this.destination
            )
            .pipe(
              mergeMap(() => {
                localStorage.setItem(
                  ONBOARDING_ID_TOKEN_KEY,
                  idTokenResult.token
                );

                // INFO Route to verify email page
                return this.router.navigate(
                  ['/', ...this.verifyEmailCtaRoute.split('/'), btoa(value.email)],
                  {
                    queryParams: this.queryParams,
                    queryParamsHandling: 'preserve',
                  }
                );
              })
            );
        }),
        takeLast(1),
      )
      .subscribe({
        next: () => {
          this.form.reset();
          this.inProgress = false;
        },
        error: (error) => {
          const errorMessage = error.status === HttpStatusCode.NotFound ?
            'Account does not exist. Kindly contact support if you have an existing account.' :
            error.code?.toLowerCase() === 'auth/invalid-credential' ?
              'Invalid Credentials'
              : 'An error occurred!';

          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open(errorMessage, 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
          this.showError = true;
        },
      });
  }

  public signInWithApple() {
    const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };

    return this.router.navigate(
      ['/', ...this.signInWithAppleRoute.split('/')],
      {
        queryParams,
        queryParamsHandling: 'merge',
      }
    );
  }

  public signInWithGoogle() {
    const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };

    return this.router.navigate(
      ['/', ...this.signInWithGoogleRoute.split('/')],
      {
        queryParams,
        queryParamsHandling: 'merge',
      }
    );
  }

  public initializeForm() {
    this.form = this.formBuilder.nonNullable.group({
      email: new FormControl(null, [Validators.required, Validators.email]),
      password: new FormControl(null, [
        Validators.required,
        Validators.minLength(8),
      ]),
    });
  }
}
